import * as Tabs from "@radix-ui/react-tabs";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { useResponsiveWidth } from "../../utils/useResponsiveWidth";
import { NICE_STEPS, clamp, formatAxisDuration } from "./chartAxis";
import DurationHistogram from "./DurationHistogram";
import {
  type DurationObservation,
  type TrendGrouping,
  type TrendPeriod,
  defaultTrendGrouping,
  durationAnalytics,
  durationTrend,
  hasHighDurationVariability,
} from "./processDurationStats";
import { ChartTooltip, CollapsibleSidebarSection } from "./processSidebarUi";

const CHART_H = 132;
const PLOT_TOP = 12;
const PLOT_BOTTOM = 24;
const PLOT_H = CHART_H - PLOT_TOP - PLOT_BOTTOM;
const AXIS_LEFT = 40;
const PAD_X = 10;
// Period labels closer than this would run into each other, so only every so many are written.
const MIN_LABEL_GAP_PX = 56;
// Below this much room per period the markers would merge into the line, so only the pointed-at
// one is drawn.
const MIN_MARKER_SLOT_PX = 14;

type TrendViewport = { xMin: number; xMax: number; yMin: number; yMax: number };
type TrendDrag = { startX: number; startY: number; x: number; y: number };

const GROUPINGS: TrendGrouping[] = ["day", "week", "month"];

/** Periods named for the axis, and more fully for the tooltip. */
const usePeriodLabels = (grouping: TrendGrouping, periods: TrendPeriod[]) => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const format = (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(i18n?.language, { ...options, timeZone: "UTC" });
    const spansYears =
      periods.length > 0 &&
      periods[0].start.slice(0, 4) !== periods[periods.length - 1].start.slice(0, 4);
    const axis =
      grouping === "month"
        ? format(spansYears ? { month: "short", year: "numeric" } : { month: "short" })
        : format({ month: "short", day: "numeric" });
    const full = grouping === "month" ? format({ month: "long", year: "numeric" }) : axis;
    const date = (start: string) => new Date(`${start}T00:00:00Z`);
    return {
      axis: (start: string) => axis.format(date(start)),
      full: (start: string) =>
        grouping === "week"
          ? t("processPanel.trendWeekOf", { date: full.format(date(start)) })
          : full.format(date(start)),
    };
  }, [grouping, i18n?.language, periods, t]);
};

/**
 * Median durations over time. Gaps in selected cases bridge the median with dashes.
 */
export const DurationTrendChart = ({
  points,
  referencePoints = points,
  showTitle = true,
  countLabel,
  description,
}: {
  description?: string;
  points: Array<DurationObservation & { date: string }>;
  referencePoints?: Array<DurationObservation & { date: string }>;
  /** False where a section heading above already names the chart. */
  showTitle?: boolean;
  countLabel?: string;
}) => {
  const { t } = useTranslation();
  const [grouping, setGrouping] = useState<TrendGrouping>(() =>
    defaultTrendGrouping(referencePoints.map(point => point.date)),
  );
  const periods = useMemo(
    () => durationTrend(points, grouping, referencePoints),
    [points, grouping, referencePoints],
  );
  const labels = usePeriodLabels(grouping, periods);
  const [containerRef, width] = useResponsiveWidth(320);
  const [hover, setHover] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);

  const [zoom, setZoom] = useState<TrendViewport | null>(null);
  const [drag, setDrag] = useState<TrendDrag | null>(null);
  const dragRef = useRef<TrendDrag | null>(null);
  const clipId = useId();
  const zoomHelpId = useId();
  const updateDrag = (next: TrendDrag | null) => {
    dragRef.current = next;
    setDrag(next);
  };
  // A new scope or grouping starts with the whole distribution in view.
  // biome-ignore lint/correctness/useExhaustiveDependencies: These inputs define the chart's data domain.
  useEffect(() => {
    setZoom(null);
    updateDrag(null);
    setHover(null);
    setFocus(null);
  }, [points, referencePoints, grouping]);
  const plotW = Math.max(1, width - AXIS_LEFT - 2 * PAD_X);
  // Two round steps of gridline that clear the highest median, so the axis reads 0 / 5m / 10m.
  const top = Math.max(0, ...periods.map(period => period.stats?.median ?? 0));
  const tickStep =
    NICE_STEPS.find(step => 2 * step >= top * 1.05) ?? Math.ceil(top / 2 / 3600) * 3600;
  const viewport = zoom ?? {
    xMin: 0,
    xMax: Math.max(1, periods.length),
    yMin: 0,
    yMax: 2 * tickStep,
  };
  const xSpan = viewport.xMax - viewport.xMin;
  const ySpan = viewport.yMax - viewport.yMin;
  const slotW = plotW / xSpan;
  const xAt = (index: number) => AXIS_LEFT + PAD_X + (index + 0.5 - viewport.xMin) * slotW;
  const yAt = (seconds: number) => PLOT_TOP + (1 - (seconds - viewport.yMin) / ySpan) * PLOT_H;
  const tickTarget = ySpan / 4;
  const tickMagnitude = 10 ** Math.floor(Math.log10(tickTarget));
  const zoomTickStep =
    (tickTarget >= 1 && NICE_STEPS.find(step => step >= tickTarget)) ||
    [1, 2, 5, 10].map(step => step * tickMagnitude).find(step => step >= tickTarget) ||
    tickStep;
  const firstTick = Math.ceil(viewport.yMin / zoomTickStep) * zoomTickStep;
  const yTicks = zoom
    ? Array.from(
        { length: Math.floor((viewport.yMax - firstTick) / zoomTickStep) + 1 },
        (_, index) => firstTick + index * zoomTickStep,
      )
    : [0, tickStep, 2 * tickStep];
  const visiblePeriod = (index: number) =>
    index + 0.5 >= viewport.xMin && index + 0.5 <= viewport.xMax;
  const pointer = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: clamp(event.clientX - rect.left, AXIS_LEFT + PAD_X, AXIS_LEFT + PAD_X + plotW),
      y: clamp(event.clientY - rect.top, PLOT_TOP, PLOT_TOP + PLOT_H),
    };
  };
  const finishZoom = () => {
    const selection = dragRef.current;
    if (!selection) return;
    if (
      Math.abs(selection.x - selection.startX) >= 4 &&
      Math.abs(selection.y - selection.startY) >= 4
    ) {
      const toX = (x: number) => viewport.xMin + ((x - AXIS_LEFT - PAD_X) / plotW) * xSpan;
      const toY = (y: number) => viewport.yMax - ((y - PLOT_TOP) / PLOT_H) * ySpan;
      setZoom({
        xMin: toX(Math.min(selection.startX, selection.x)),
        xMax: toX(Math.max(selection.startX, selection.x)),
        yMin: toY(Math.max(selection.startY, selection.y)),
        yMax: toY(Math.min(selection.startY, selection.y)),
      });
      setHover(null);
      setFocus(null);
    }
    updateDrag(null);
  };
  const labelEvery = Math.max(1, Math.ceil(MIN_LABEL_GAP_PX / slotW));

  const filled = periods.flatMap((period, index) => (period.stats ? [index] : []));
  const statsAt = (index: number) => periods[index].stats as NonNullable<TrendPeriod["stats"]>;
  const medianPoint = (index: number) => `${xAt(index)} ${yAt(statsAt(index).median)}`;
  // Neighbouring periods join with a solid line; a stretch without cases is bridged dashed, so
  // the trend still reads without claiming anything about the periods in between.
  const segments = filled.slice(1).map((index, i) => ({
    from: filled[i],
    to: index,
    bridged: index - filled[i] > 1,
  }));
  const segmentPath = (bridged: boolean) =>
    segments
      .filter(segment => segment.bridged === bridged)
      .map(segment => `M ${medianPoint(segment.from)} L ${medianPoint(segment.to)}`)
      .join(" ");
  const pick = (event: React.PointerEvent<SVGSVGElement>) => {
    if (dragRef.current) {
      updateDrag({ ...dragRef.current, ...pointer(event) });
      return;
    }
    const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
    const slot = Math.floor(viewport.xMin + (x - AXIS_LEFT - PAD_X) / slotW);
    setHover(x >= AXIS_LEFT + PAD_X && x <= width - PAD_X ? (periods[slot]?.start ?? null) : null);
  };
  // Track period identities so filters do not move the highlighted period.
  const active = hover ?? focus;
  const hovered = periods.findIndex(period => period.start === active);
  const activePeriod = !drag && visiblePeriod(hovered) ? periods[hovered] : undefined;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        {showTitle ? (
          <span className="text-[10px] font-medium uppercase tracking-wide text-text-dim">
            {t("trend_over_time")}
          </span>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {zoom && (
            <button
              type="button"
              onClick={() => {
                setZoom(null);
                updateDrag(null);
              }}
              className="rounded-md border border-outline px-2 py-1 text-xs text-text-mid hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              {t("processPanel.histogramResetZoom")}
            </button>
          )}
          <fieldset className="inline-flex rounded-md border border-outline bg-surface p-0.5">
            <legend className="sr-only">{t("processPanel.trendGroupBy")}</legend>
            {GROUPINGS.map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={grouping === option}
                onClick={() => {
                  setGrouping(option);
                  setHover(null);
                  setFocus(null);
                }}
                className={`h-6 rounded px-2 text-[11px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
                  grouping === option
                    ? "bg-surface-strong font-medium text-text"
                    : "text-text-dim hover:text-text"
                }`}
              >
                {t(`processPanel.trendGroup_${option}`)}
              </button>
            ))}
          </fieldset>
        </div>
      </div>
      <div ref={containerRef} className="relative">
        <svg
          width={width}
          height={CHART_H}
          role="img"
          aria-label={t("processPanel.trendAria", {
            grouping: t(`processPanel.trendGroup_${grouping}`).toLowerCase(),
          })}
          aria-describedby={zoomHelpId}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Arrow keys inspect chart periods and their tooltips.
          tabIndex={0}
          onBlur={() => setFocus(null)}
          onPointerDown={event => {
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            if (
              event.button !== 0 ||
              x < AXIS_LEFT + PAD_X ||
              x > width - PAD_X ||
              y < PLOT_TOP ||
              y > PLOT_TOP + PLOT_H
            )
              return;
            event.preventDefault();
            event.currentTarget.focus({ preventScroll: true });
            event.currentTarget.setPointerCapture?.(event.pointerId);
            updateDrag({ startX: x, startY: y, x, y });
            setHover(null);
          }}
          onPointerMove={pick}
          onPointerUp={finishZoom}
          onPointerCancel={() => updateDrag(null)}
          onLostPointerCapture={() => updateDrag(null)}
          onPointerLeave={() => setHover(null)}
          onDoubleClick={() => {
            updateDrag(null);
            setZoom(null);
            setHover(null);
          }}
          onKeyDown={event => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              const visible = periods.flatMap((period, index) =>
                visiblePeriod(index) ? [period.start] : [],
              );
              const current = visible.indexOf(focus ?? "");
              const next =
                current < 0
                  ? event.key === "ArrowRight"
                    ? 0
                    : visible.length - 1
                  : clamp(current + (event.key === "ArrowRight" ? 1 : -1), 0, visible.length - 1);
              setHover(null);
              setFocus(visible[next] ?? null);
            }
            if (event.key === "Escape") {
              event.stopPropagation();
              if (dragRef.current) updateDrag(null);
              else setZoom(null);
            }
          }}
          className="block cursor-crosshair touch-none select-none rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={AXIS_LEFT + PAD_X} y={PLOT_TOP} width={plotW} height={PLOT_H} />
            </clipPath>
          </defs>
          <desc id={zoomHelpId}>
            {[description, t("processPanel.trendZoomHelp")].filter(Boolean).join(" ")}
          </desc>
          {yTicks.map(value => (
            <g key={value}>
              <line
                x1={AXIS_LEFT}
                x2={width}
                y1={yAt(value)}
                y2={yAt(value)}
                stroke="var(--color-outline)"
                opacity={value === 0 ? 0.9 : 0.5}
              />
              <text
                x={AXIS_LEFT - 6}
                y={yAt(value) + 3}
                textAnchor="end"
                fill="var(--color-text-dim)"
                fontSize={10}
              >
                {formatAxisDuration(value, zoom ? zoomTickStep : tickStep)}
              </text>
            </g>
          ))}
          {periods.map(
            (period, index) =>
              visiblePeriod(index) &&
              (periods.length - 1 - index) % labelEvery === 0 && (
                <text
                  key={period.start}
                  x={xAt(index)}
                  y={PLOT_TOP + PLOT_H + 16}
                  textAnchor={
                    xAt(index) < AXIS_LEFT + 30
                      ? "start"
                      : xAt(index) > width - 30
                        ? "end"
                        : "middle"
                  }
                  fill="var(--color-text-dim)"
                  fontSize={10}
                >
                  {labels.axis(period.start)}
                </text>
              ),
          )}
          <g clipPath={`url(#${clipId})`}>
            {activePeriod && (
              <line
                x1={xAt(hovered)}
                x2={xAt(hovered)}
                y1={PLOT_TOP}
                y2={PLOT_TOP + PLOT_H}
                stroke="var(--color-outline-strong)"
              />
            )}
            <path
              d={segmentPath(false)}
              fill="none"
              stroke="var(--color-chart-blue)"
              strokeWidth={2}
              strokeLinecap="round"
            />
            <path
              d={segmentPath(true)}
              fill="none"
              stroke="var(--color-chart-blue)"
              strokeWidth={1.5}
              strokeDasharray="3 4"
              opacity={0.6}
            />
            {filled.map(
              index =>
                (slotW >= MIN_MARKER_SLOT_PX || index === hovered) && (
                  <circle
                    key={`median-${index}`}
                    data-testid={`trend-median-${periods[index].start}`}
                    cx={xAt(index)}
                    cy={yAt(statsAt(index).median)}
                    r={index === hovered ? 5 : 4}
                    fill="var(--color-chart-blue)"
                    stroke="var(--color-surface)"
                    strokeWidth={2}
                  />
                ),
            )}
          </g>
          {drag && (
            <rect
              data-testid="trend-zoom-selection"
              x={Math.min(drag.startX, drag.x)}
              y={Math.min(drag.startY, drag.y)}
              width={Math.abs(drag.x - drag.startX)}
              height={Math.abs(drag.y - drag.startY)}
              fill="var(--color-chart-blue)"
              fillOpacity={0.15}
              stroke="var(--color-chart-blue)"
              strokeDasharray="3 3"
              pointerEvents="none"
            />
          )}
        </svg>
        {activePeriod && (
          <ChartTooltip
            wrap
            x={xAt(hovered)}
            y={
              activePeriod.stats
                ? clamp(yAt(activePeriod.stats.median), PLOT_TOP, PLOT_TOP + PLOT_H)
                : PLOT_TOP + PLOT_H / 2
            }
            width={width}
            maxWidth={240}
            label={
              <div className="px-1.5 py-2 text-xs">
                <p className="mb-2 border-b border-outline pb-2 font-medium">
                  {labels.full(activePeriod.start)}
                </p>
                <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-5 gap-y-1.5">
                  <dt className="text-text-dim">{countLabel ?? t("processPanel.metricCases")}</dt>
                  <dd className="text-right font-medium">{activePeriod.stats?.count ?? 0}</dd>
                  <dt className="text-text-dim">{t("processPanel.trendMedian")}</dt>
                  <dd className="whitespace-nowrap text-right font-medium">
                    {activePeriod.stats ? formatDuration(activePeriod.stats.median) : "—"}
                  </dd>
                </dl>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

const DurationAnalyticsSection = ({
  observations,
  countLabel,
  onAskVariability,
  headerIcon,
}: {
  headerIcon?: React.ReactNode;
  observations: DurationObservation[];
  countLabel: string;
  onAskVariability?: () => void;
}) => {
  const { t } = useTranslation();
  const analytics = useMemo(() => durationAnalytics(observations), [observations]);
  if (analytics.durations.length < 3) return null;
  const datedDays = new Set(analytics.trend.map(point => point.date)).size;
  const chartTabClass =
    "min-w-0 flex-1 rounded-md px-2 py-1.5 text-xs font-medium text-text-dim outline-none data-[state=active]:bg-surface data-[state=active]:text-text data-[state=active]:shadow-sm focus-visible:ring-2 focus-visible:ring-info/30";

  return (
    <CollapsibleSidebarSection
      title={t("duration_analytics")}
      titleAccessory={
        headerIcon ? undefined : `${analytics.durations.length} ${countLabel.toLowerCase()}`
      }
      headerIcon={headerIcon}
      summary={
        headerIcon
          ? t("processPanel.stepDurationSummary", {
              median: formatDuration(analytics.median),
              min: formatDuration(analytics.min),
              max: formatDuration(analytics.max),
            })
          : undefined
      }
      separated={false}
      defaultOpen={false}
      className={
        headerIcon ? "border-t border-outline pt-4" : "rounded-xl border border-outline p-3"
      }
    >
      <p className="mb-3 text-xs leading-relaxed text-text-mid">
        {t("processPanel.durationScope")}
      </p>
      <Tabs.Root defaultValue="distribution">
        <Tabs.List
          aria-label={t("duration_analytics")}
          className="mb-3 flex gap-1 rounded-lg bg-surface-strong p-1"
        >
          <Tabs.Trigger value="distribution" className={chartTabClass}>
            {t("processPanel.durationDistribution")}
          </Tabs.Trigger>
          <Tabs.Trigger value="trend" className={chartTabClass}>
            {t("trend_over_time")}
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="distribution" className="outline-none">
          <DurationHistogram
            values={analytics.durations}
            countLabel={countLabel}
            description={t("processPanel.durationDistributionHelp", {
              unit: countLabel.toLowerCase(),
            })}
            onAskVariability={
              onAskVariability && hasHighDurationVariability(analytics.durations)
                ? onAskVariability
                : undefined
            }
          />
        </Tabs.Content>
        <Tabs.Content value="trend" className="outline-none">
          {datedDays > 1 ? (
            <>
              <DurationTrendChart
                points={analytics.trend}
                description={t("processPanel.durationTrendHelp")}
                showTitle={false}
                countLabel={countLabel}
              />
            </>
          ) : (
            <p className="py-4 text-xs text-text-dim">{t("processPanel.durationNoTrend")}</p>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </CollapsibleSidebarSection>
  );
};

export default DurationAnalyticsSection;
